import React from "react";
import { SelectBoxOption } from "../../interfaces";
import { SelectBox } from "../reactSelect";
import { useLazyGetAllPricingQuery, useLazyGetBOMsQuery, useLazyGetDocumentsQuery, useLazyGetMaterialsQuery, useLazyGetProductsQuery, useLazyGetProposalsQuery } from "../../redux/services";
import { Box, Stack, Typography } from "@mui/material";
import { AntSwitch } from "../antSwitch";

export const ModuleDropDown: React.FC<{
    entity: 'project' | 'product' | 'proposal' | 'pricing' | 'material' | 'bom' | 'document' | 'none',
    value: SelectBoxOption | SelectBoxOption[],
    onChange: (option: SelectBoxOption | SelectBoxOption[]) => void,
    pricingFilterCurrency?: string;
    error?: boolean,
    helperText?: string,
    label?: string,
    disableOption?: (obj: any) => void,
    pricingOption?: { object_id?: string, object_type?: 'product' | 'proposal' }
    disabled?: boolean
    isRequired?: boolean,
    viewOnly?: boolean,
    isMulti?: boolean,
    placeholder?: string,
    versionHistorySelector?: boolean,
    objectId?: string,
    objectType?: string
}> = ({
    entity, value, onChange, error, helperText, label, pricingOption,
    disableOption, disabled = false, isRequired = false, viewOnly = false,
    pricingFilterCurrency = '', isMulti, placeholder = '', versionHistorySelector, objectId, objectType
}) => {

        const [getProduct, getProductData] = useLazyGetProductsQuery();
        const [getProposal, getProposalData] = useLazyGetProposalsQuery();
        const [getPricing, getPricingData] = useLazyGetAllPricingQuery();
        const [getMaterials, getMaterialsData] = useLazyGetMaterialsQuery();
        const [getBOMs, getBOMsData] = useLazyGetBOMsQuery();
        const [getDocuments, getDocumentData] = useLazyGetDocumentsQuery();

        const [includeHistory, updateIncludeHistory] = React.useState<boolean>(false);

        const loadBOMOption = async (search: string, loadedOptions: any) => {
            try {
                let page = Math.round(loadedOptions.length / 50);
                let options: any[] = [];

                switch (entity) {
                    case 'product':
                        await getProduct({ page, pageSize: 50, search }).unwrap().then(res => {
                            options = res.data.map((_: any) => ({ ..._, value: _._id, label: `${_.sequence_id} (${_.product_name})`, disabled: disableOption ? disableOption(_) : false }))
                        });
                        break;

                    case 'proposal':
                        await getProposal({ page, pageSize: 50, search }).unwrap().then(res => {
                            options = res.data.map((_: any) => ({ ..._, value: _._id, label: `${_.sequence_id} (${_.proposal_name})`, disabled: disableOption ? disableOption(_) : false }))
                        });
                        break;

                    case 'pricing':
                        await getPricing({ page, pageSize: 50, object_id: pricingOption?.object_id ?? "", object_type: pricingOption?.object_type ?? "", currency: pricingFilterCurrency }).unwrap().then(res => {
                            options = res.data.map((_: any) => ({ ..._, value: _._id, label: `${_.sequence_id} (${_.pricing_name})`, disabled: disableOption ? disableOption(_) : false }))
                        });
                        break;

                    case 'material':
                        await getMaterials({ page, pageSize: 50, search, currency: pricingFilterCurrency, object_id: objectId, object_type: objectType, include_histories: includeHistory }).unwrap().then(res => {
                            options = res.data.map((_: any) => ({ ..._, value: _._id, label: `${_.sequence_id}-r${_.version} (${_.short_description})`, disabled: disableOption ? disableOption(_) : false }))
                        });
                        break;

                    case 'bom':
                        await getBOMs({ page, pageSize: 50, search, currency: pricingFilterCurrency, object_id: objectId, object_type: objectType, include_histories: includeHistory }).unwrap().then(res => {
                            options = res.data.map((_: any) => ({ ..._, value: _._id, label: `${_.sequence_id}-r${_.version} (${_.short_description})`, disabled: disableOption ? disableOption(_) : false }))
                        });
                        break;

                    case 'document':
                        await getDocuments({ page, pageSize: 50, search, include_all: true, object_id: objectId, object_type: objectType, include_history: includeHistory }).unwrap().then(res => {
                            options = res.data.map((_: any) => ({ ..._, value: _._id, label: `${_.sequence_id}-r${_.version} (${_.file_name})`, disabled: disableOption ? disableOption(_) : false }))
                        });
                        break;

                    default:
                        break;
                }
                return { options, hasMore: options.length === 50 }
            } catch (error) {
                console.error('error:', error)
                return { options: [], hasMore: false }
            }
        };

        const isLoading = getProductData.isLoading || getProductData.isFetching
            || getPricingData.isLoading || getPricingData.isFetching
            || getProposalData.isLoading || getProposalData.isFetching
            || getMaterialsData.isLoading || getMaterialsData.isFetching
            || getBOMsData.isLoading || getBOMsData.isFetching
            || getDocumentData.isLoading || getDocumentData.isFetching;

        const isError = getProductData.isError || getPricingData.isError || getProposalData.isError || getMaterialsData.isError || getBOMsData.isError || getDocumentData.isError || error;

        return <Box position={"relative"}>
            {/* Switch */}
            {versionHistorySelector && <Box sx={{ position: "absolute", right: 0, top: 0 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    <AntSwitch
                        checked={includeHistory}
                        onChange={(e, isChecked) => updateIncludeHistory(isChecked)}
                    />
                    <Typography variant="body2">Include History</Typography>
                </Stack>
            </Box>}

            {/* Select Box */}
            <SelectBox
                key={entity === 'pricing' ? pricingOption?.object_id : `${entity}-${includeHistory ? 'history' : 'current'}`}
                isPaginate
                loadOptions={loadBOMOption}
                loading={isLoading}
                options={[]}
                label={label}
                placeholder={placeholder ? placeholder : `Select ${label ?? ""}`}
                value={value}
                onChange={(data: any) => onChange(data)}
                error={isError}
                helperText={helperText}
                isDisabled={disabled}
                isRequired={isRequired}
                viewOnly={viewOnly}
                isMulti={isMulti}
            />
        </Box>
    }